// Chains and tokens the demo offers. Names are the api's chain names (scripts/config.yml). The escrow
// is always the chain's stable; any other source token is swapped into it by the api's hosted swap
// (builders-ui.md §4), so a refund comes back in the stable. Sui has no hosted swap yet: its only
// source token is its stable. Jupnet is a source only: it escrows any of its stablecoins (`GET
// /jupnet`'s `source.stables`, no hosted swap there) and is never a destination; receiving on
// Jupnet is a `gum_deposit` through the token's home chain (`jupnetRoutes`).

import type { Delivery, JupnetChains } from "./api";
import { normalizeSuiCoin } from "./sui";

export type Family = "evm" | "solana" | "sui" | "jupnet";
/** `name`: what a picker shows when the symbol alone is ambiguous (Jupnet's bridged stables) */
export type Token = { sym: string; addr: string; decimals: number; native?: boolean; name?: string };
export type ChainKey = "base" | "arbitrum" | "ethereum" | "bsc" | "robinhood" | "solana" | "sui" | "jupnet";
export type Chain = {
  key: ChainKey;
  label: string;
  family: Family;
  /** gum chain id (the wire's), as the api reports it */
  gumChainId: number;
  evmChainId?: number;
  /** what `wallet_addEthereumChain` needs when the wallet does not know the chain yet */
  addChain?: { rpcUrls: string[]; nativeCurrency: { name: string; symbol: string; decimals: number }; blockExplorerUrls: string[] };
  /** the escrow asset on this chain as a source */
  stable: Token;
  /** source presets: the stable first (no swap), then tokens the api swaps into it */
  srcTokens: Token[];
  /** destination presets */
  dstTokens: Token[];
  explorerTx?: (tx: string) => string;
  /** gum-intent deployed there and wired up in the demo (as source and destination) */
  enabled: boolean;
  disabledReason?: string;
};

export const NATIVE_EVM = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";
/** Native ETH by its sentinel address, so a custom entry of it is native too. */
export const isNativeEvm = (addr: string) => addr.trim().toLowerCase() === NATIVE_EVM.toLowerCase();
/** The SPL native mint: on a Solana destination it means native SOL, paid as lamports. */
export const NATIVE_SOL_MINT = "So11111111111111111111111111111111111111112";
const CBBTC_EVM = { sym: "cbBTC", addr: "0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf", decimals: 8 };
const ETH = { sym: "ETH", addr: NATIVE_EVM, decimals: 18, native: true };
const BNB = { sym: "BNB", addr: NATIVE_EVM, decimals: 18, native: true };
const ETH_USDC = { sym: "USDC", addr: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", decimals: 6 };
/** Binance-Peg stables: 18 decimals on BSC */
const BSC_USDC = { sym: "USDC", addr: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d", decimals: 18 };
const BSC_USDT = { sym: "USDT", addr: "0x55d398326f99059fF775485246999027B3197955", decimals: 18 };
/** Global Dollar, Robinhood Chain's stable */
const USDG = { sym: "USDG", addr: "0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168", decimals: 6 };
/** Circle's native USDC on Sui. */
const SUI_USDC = { sym: "USDC", addr: "0xdba34672e30cb065b1f93e3ab55318768fd6fef66c15942c9f7cb846e2f900e7::usdc::USDC", decimals: 6 };
/** gum's Jupnet stablecoins (scripts/config.yml `services.stables.jupnet`), decimals as their JPL
 *  mints read on 2026-10-19: BSC's USDT keeps its 18. A mint the api adds later is read at runtime. */
export const JUPNET_STABLES: Token[] = [
  { sym: "USD", name: "USD (unified)", addr: "GCXAvWiriCEpTBLQ2JyRqjP7TMBsBcG1nEGEaahPsRCX", decimals: 6 },
  { sym: "USDT", name: "USDT (from Ethereum)", addr: "DoFkAhBAgrqL9aab3XgYji6FkZdPJfVxPSXwCL7DKLp2", decimals: 6 },
  { sym: "USDT", name: "USDT (from BSC)", addr: "F6NGexfFy7fBj4gqvqCmXapJLEBhUmhsAi6BtgbjMMr9", decimals: 18 },
  { sym: "USDT", name: "USDT (from Arbitrum)", addr: "J92ms2zhvBotsRYFfCR96YWprP6CCKXmCPuGBA4dmNKa", decimals: 6 },
  { sym: "USDT", name: "USDT (from Solana)", addr: "C2zoPFmynZR4BxvpficeL3A8SSBiyo2f2HYkzJii15gr", decimals: 6 },
  { sym: "USDG", name: "USDG (from Robinhood)", addr: "9QiU1U3U9w6zaFyCwaSFG9GTap6ZnxrFq3Yv61fnKPC8", decimals: 6 },
];

export const CHAINS: Record<ChainKey, Chain> = {
  base: {
    key: "base",
    label: "Base",
    family: "evm",
    gumChainId: 8453,
    evmChainId: 8453,
    stable: { sym: "USDC", addr: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", decimals: 6 },
    srcTokens: [
      { sym: "USDC", addr: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", decimals: 6 },
      ETH,
      { sym: "WETH", addr: "0x4200000000000000000000000000000000000006", decimals: 18 },
      CBBTC_EVM,
      { sym: "AERO", addr: "0x940181a94A35A4569E4529A3CDfB74e38FD98631", decimals: 18 },
    ],
    dstTokens: [
      { sym: "USDC", addr: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", decimals: 6 },
      { sym: "ETH", addr: NATIVE_EVM, decimals: 18, native: true },
      CBBTC_EVM,
    ],
    explorerTx: (tx) => `https://basescan.org/tx/${tx}`,
    enabled: true,
  },
  arbitrum: {
    key: "arbitrum",
    label: "Arbitrum",
    family: "evm",
    gumChainId: 42161,
    evmChainId: 42161,
    stable: { sym: "USDC", addr: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", decimals: 6 },
    srcTokens: [
      { sym: "USDC", addr: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", decimals: 6 },
      ETH,
      { sym: "WETH", addr: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1", decimals: 18 },
      { sym: "ARB", addr: "0x912CE59144191C1204E64559FE8253a0e49E6548", decimals: 18 },
      { sym: "USDT", addr: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9", decimals: 6 },
    ],
    dstTokens: [
      { sym: "USDC", addr: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", decimals: 6 },
      { sym: "ETH", addr: NATIVE_EVM, decimals: 18, native: true },
      CBBTC_EVM,
    ],
    explorerTx: (tx) => `https://arbiscan.io/tx/${tx}`,
    enabled: true,
  },
  ethereum: {
    key: "ethereum",
    label: "Ethereum",
    family: "evm",
    gumChainId: 1,
    evmChainId: 1,
    stable: ETH_USDC,
    srcTokens: [
      ETH_USDC,
      ETH,
      { sym: "WETH", addr: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", decimals: 18 },
      { sym: "USDT", addr: "0xdAC17F958D2ee523a2206206994597C13D831ec7", decimals: 6 },
      CBBTC_EVM,
    ],
    dstTokens: [ETH_USDC, ETH, CBBTC_EVM],
    explorerTx: (tx) => `https://etherscan.io/tx/${tx}`,
    enabled: true,
  },
  bsc: {
    key: "bsc",
    label: "BNB Chain",
    family: "evm",
    gumChainId: 56,
    evmChainId: 56,
    stable: BSC_USDT,
    srcTokens: [BSC_USDT, BNB, BSC_USDC, { sym: "WBNB", addr: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c", decimals: 18 }],
    dstTokens: [BSC_USDT, BNB],
    explorerTx: (tx) => `https://bscscan.com/tx/${tx}`,
    enabled: true,
  },
  robinhood: {
    key: "robinhood",
    label: "Robinhood Chain",
    family: "evm",
    gumChainId: 4663,
    evmChainId: 4663,
    addChain: {
      rpcUrls: ["https://rpc.mainnet.chain.robinhood.com"],
      nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
      blockExplorerUrls: ["https://robinhoodchain.blockscout.com"],
    },
    stable: USDG,
    srcTokens: [USDG, ETH],
    dstTokens: [USDG, ETH],
    explorerTx: (tx) => `https://robinhoodchain.blockscout.com/tx/${tx}`,
    enabled: true,
  },
  solana: {
    key: "solana",
    label: "Solana",
    family: "solana",
    gumChainId: 900,
    stable: { sym: "USDC", addr: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", decimals: 6 },
    srcTokens: [
      { sym: "USDC", addr: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", decimals: 6 },
      // the native mint as an input: Jupiter wraps the wallet's SOL in the same transaction
      { sym: "SOL", addr: NATIVE_SOL_MINT, decimals: 9, native: true },
      { sym: "JUP", addr: "JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN", decimals: 6 },
      { sym: "BONK", addr: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263", decimals: 5 },
    ],
    dstTokens: [
      { sym: "SOL", addr: NATIVE_SOL_MINT, decimals: 9, native: true },
      { sym: "USDC", addr: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", decimals: 6 },
      { sym: "cbBTC", addr: "cbbtcf3aa214zXHbiAZQwf4122FBYbraNdFqgw4iMij", decimals: 8 },
    ],
    explorerTx: (tx) => `https://solscan.io/tx/${tx}`,
    enabled: true,
  },
  sui: {
    key: "sui",
    label: "Sui",
    family: "sui",
    gumChainId: 101,
    stable: SUI_USDC,
    // the stable only: the api has no hosted swap on Sui
    srcTokens: [SUI_USDC],
    dstTokens: [{ sym: "SUI", addr: "0x2::sui::SUI", decimals: 9, native: true }, SUI_USDC],
    explorerTx: (tx) => `https://suiscan.xyz/mainnet/tx/${tx}`,
    enabled: true,
  },
  // as the api offers it, `jupnetSource` (until then disabled); this entry names Jupnet in a tracker
  jupnet: {
    key: "jupnet",
    label: "Jupnet",
    family: "jupnet",
    gumChainId: 696969,
    stable: JUPNET_STABLES[0]!,
    srcTokens: JUPNET_STABLES,
    dstTokens: [],
    explorerTx: (tx) => `https://explorer.gum.ag/tx/${tx}`,
    enabled: false,
    disabledReason: "the api has no Jupnet program configured yet",
  },
};

export const CHAIN_LIST = Object.values(CHAINS);
/** Where an intent can be delivered: every chain but Jupnet (receiving there is a `gum_deposit`). */
export const DESTINATION_CHAINS = CHAIN_LIST.filter((c) => c.family !== "jupnet");

export function chainByGumId(id: number): Chain | undefined {
  return CHAIN_LIST.find((c) => c.gumChainId === id);
}

/** A token's address in the form its chain compares by: EVM case-insensitively, a Sui coin type
 *  with its address in full, an SPL mint as is. */
const tokenKey = (chain: Chain, addr: string) => (chain.family === "evm" ? addr.toLowerCase() : chain.family === "sui" ? normalizeSuiCoin(addr) : addr);

/** A destination token's metadata from the presets, by its address. */
export function knownToken(chain: Chain, addr: string): Token | undefined {
  const k = tokenKey(chain, addr);
  return chain.dstTokens.find((t) => tokenKey(chain, t.addr) === k);
}

/** A source token's metadata from the presets, by its address. */
export function knownSrcToken(chain: Chain, addr: string): Token | undefined {
  const k = tokenKey(chain, addr);
  return chain.srcTokens.find((t) => tokenKey(chain, t.addr) === k);
}

/** Whether `token` is `chain`'s escrow stable (no hosted swap needed): on Jupnet, any of its stables. */
export function isStable(chain: Chain, token: string): boolean {
  if (chain.family === "jupnet") return !!knownSrcToken(chain, token);
  return tokenKey(chain, token) === tokenKey(chain, chain.stable.addr);
}

/** A direct delivery of the destination chain's stable from Jupnet: a withdrawal to that chain. */
export const isWithdrawal = (src: Chain, dst: Chain, dstToken: string, delivery: Delivery = "direct"): boolean =>
  src.family === "jupnet" && delivery === "direct" && dst.family !== "jupnet" && isStable(dst, dstToken);

/** Jupnet as a source, from `GET /jupnet`'s `source` (null until the api has the program): its
 *  stables, named from the presets, else with `decimals` read off the mint (one not read is left
 *  out: its amounts could not be shown). */
export function jupnetSource(j: JupnetChains, decimals: Record<string, number> = {}): Chain | null {
  if (!j.source || j.source.chain_id !== CHAINS.jupnet.gumChainId) return null;
  const srcTokens = j.source.stables.flatMap((addr): Token[] => {
    const known = JUPNET_STABLES.find((t) => t.addr === addr);
    if (known) return [known];
    const d = decimals[addr];
    return d === undefined ? [] : [{ sym: "STABLE", name: `${addr.slice(0, 4)}…${addr.slice(-4)}`, addr, decimals: d }];
  });
  if (!srcTokens.length) return null;
  return { ...CHAINS.jupnet, stable: srcTokens[0]!, srcTokens, enabled: true, disabledReason: undefined };
}

/** `source.stables` the presets do not name: their decimals are read off the mint. */
export const unknownJupnetStables = (j: JupnetChains): string[] => (j.source?.stables ?? []).filter((a) => !JUPNET_STABLES.some((t) => t.addr === a));

/** Where "receive on Jupnet" can go: each destination chain with gum's bank (`GET /jupnet`), with
 *  the demo's usual destination tokens there (any token deposits). Sui fills direct only. */
export type JupnetRoute = { chain: Chain; tokens: Token[] };

export function jupnetRoutes(j: JupnetChains): JupnetRoute[] {
  return j.chains.flatMap((c) => {
    const chain = CHAINS[c.chain as ChainKey];
    if (!chain?.enabled || chain.family === "sui" || chain.family === "jupnet" || !chain.dstTokens.length) return [];
    return [{ chain, tokens: chain.dstTokens }];
  });
}
